import { create } from 'zustand';

export type NotificationSeverity = 'critical' | 'warning' | 'info';
export type NotificationSource =
  | 'system'
  | 'health'
  | 'build'
  | 'backfill'
  | 'signal'
  | 'execution'
  | 'user-action';

export interface NotificationItem {
  id: string;
  severity: NotificationSeverity;
  source: NotificationSource;
  title: string;
  message: string;
  ts: number;
  read: boolean;
  dedupKey?: string;
  link?: string;
  count?: number;
}

interface NotificationState {
  items: NotificationItem[];
  push: (item: Omit<NotificationItem, 'id' | 'read' | 'count'>) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  remove: (id: string) => void;
  clear: () => void;
}

const MAX_ITEMS = 200;
const DEDUP_WINDOW_MS = 60_000;

const now = () => Date.now();
const makeId = () => `n_${now()}_${Math.random().toString(36).slice(2, 8)}`;

export const useNotificationStore = create<NotificationState>()(
  (set) => ({
    items: [],
    push: (item) => {
      set((state) => {
        const dedupKey = item.dedupKey?.trim();
        if (dedupKey) {
          const idx = state.items.findIndex(
            (x) => x.dedupKey === dedupKey && Math.abs((item.ts || now()) - x.ts) <= DEDUP_WINDOW_MS,
          );
          if (idx >= 0) {
            const next = [...state.items];
            const current = next[idx];
            if (!current) return state;
            next[idx] = {
              ...current,
              ts: item.ts || now(),
              message: item.message,
              severity: item.severity,
              read: false,
              count: (current.count || 1) + 1,
            };
            return { items: next.sort((a, b) => b.ts - a.ts).slice(0, MAX_ITEMS) };
          }
        }
        const nextItem: NotificationItem = {
          ...item,
          id: makeId(),
          ts: item.ts || now(),
          read: false,
          count: 1,
        };
        return { items: [nextItem, ...state.items].slice(0, MAX_ITEMS) };
      });
    },
    markRead: (id) => set((state) => ({ items: state.items.map((x) => (x.id === id ? { ...x, read: true } : x)) })),
    markAllRead: () => set((state) => ({ items: state.items.map((x) => ({ ...x, read: true })) })),
    remove: (id) => set((state) => ({ items: state.items.filter((x) => x.id !== id) })),
    clear: () => set({ items: [] }),
  }),
);
